
import { useState } from 'react';
import { dmvChecklist } from '../../../mocks/relocation';

export default function DMVChecklistSection() {
  const [completed, setCompleted] = useState<number[]>([]);

  const toggleComplete = (step: number) => {
    setCompleted((prev) =>
      prev.includes(step) ? prev.filter((s) => s !== step) : [...prev, step]
    );
  };

  const progress = Math.round((completed.length / dmvChecklist.length) * 100);

  return (
    <section id="dmv" className="py-20 px-6 scroll-mt-32">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-teal-700 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <i className="ri-file-list-3-line"></i>
            Official Checklist
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">DMV &amp; Documentation Checklist</h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm">
            California requires new residents to update their license and registration promptly. Track your progress below.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Your Progress</span>
            <span className="text-sm font-bold text-teal-600">{progress}% Complete</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-400 mt-2">{completed.length} of {dmvChecklist.length} tasks completed</p>
        </div>

        {/* Checklist Items */}
        <div className="space-y-4 max-w-3xl mx-auto">
          {dmvChecklist.map((item) => {
            const isDone = completed.includes(item.step);
            return (
              <div
                key={item.step}
                className={`rounded-xl border p-5 transition-all ${
                  isDone ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-gray-100'
                }`}
              >
                <div className="flex items-start gap-4">
                  <button
                    onClick={() => toggleComplete(item.step)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 cursor-pointer transition-colors ${
                      isDone
                        ? 'bg-emerald-500 text-white'
                        : 'border-2 border-gray-300 text-transparent hover:border-teal-400'
                    }`}
                  >
                    <i className="ri-check-line text-sm"></i>
                  </button>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className={`text-sm font-bold ${isDone ? 'text-emerald-700 line-through' : 'text-gray-900'}`}>
                          Step {item.step}: {item.title}
                        </h3>
                        <p className="text-xs text-red-500 font-medium mt-1">
                          <i className="ri-alarm-warning-line mr-1"></i>
                          {item.deadline}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-gray-700 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">{item.fee}</span>
                    </div>

                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-1.5">Required Documents:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {item.documents.map((doc) => (
                          <span key={doc} className="text-xs bg-gray-50 text-gray-600 px-2.5 py-1 rounded-full border border-gray-100">
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-amber-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-amber-800">
                        <i className="ri-lightbulb-line mr-1"></i>
                        {item.tip}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
